import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  DatasourceTable,
  ExpandableRowData,
  newTableTheme,
  Column,
} from '@ardoq/table-legacy';
import {
  PaginationBar,
  PaginationController,
  SortByFn,
  SortById,
} from '@ardoq/pagination';
import { colors, s8 } from '@ardoq/design-tokens';
import { FlexBox, Box } from '@ardoq/layout';
import { LilInfoDoq, DoqType } from '@ardoq/doq';
import { LucidchartIcon } from '@ardoq/icons';
import { Header3 } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { PersistedExternalDocument, SortOrder } from '@ardoq/api-types';
import {
  getCurrentLocale,
  localeCompare,
  localeIncludesLowercase,
} from '@ardoq/locale';
import { SearchInput } from '@ardoq/forms';
import { isTruthy } from '@ardoq/common-helpers';
import { Island } from '@ardoq/page-layout';

const HeightControl = styled.div`
  height: 100%;
  border-bottom: ${colors.borderSubtle};
`;

const Row = styled.tr`
  background: ${colors.surfaceDefault};
`;

export type ExternalDocumentTableRow = {
  id: string;
  name: string;
  type: string;
  url: string;
  createdByName: string;
  created: string;
  lastModifiedByName: string;
  lastModified: string;
};

export const filterDocumentsBySearchTerm = (
  documents: PersistedExternalDocument[],
  searchTerm?: string
): PersistedExternalDocument[] => {
  if (!searchTerm) return documents;
  const locale = getCurrentLocale();
  return documents.filter(
    doc =>
      localeIncludesLowercase(doc.displayText, searchTerm, locale) ||
      localeIncludesLowercase(doc.createdByName, searchTerm, locale) ||
      localeIncludesLowercase(doc.provider, searchTerm, locale) ||
      localeIncludesLowercase(doc.url, searchTerm, locale)
  );
};

export const toExternalDocumentTableRow = (
  document: PersistedExternalDocument
): ExternalDocumentTableRow => ({
  id: document._id,
  name: document.displayText,
  type: document.provider,
  url: document.url,
  createdByName: document.createdByName,
  created: document.created,
  lastModifiedByName: document.lastModifiedByName,
  lastModified: document.lastUpdated,
});

export const getTypeColumn = (
  sortById: SortById,
  sortOrder: SortOrder | false | null | undefined,
  sortBy: SortByFn,
  title: string = 'Type'
): Column<ExternalDocumentTableRow> => ({
  title,
  dataIndex: 'type',
  headerStyle: { paddingBottom: s8, width: '5%' },
  sortOrder: sortById === 'type' ? sortOrder : false,
  onHeaderClick: () => sortBy('type'),
  valueRender: (type: string) => {
    if (type.toLowerCase() === 'lucidchart') {
      return (
        <FlexBox style={{ display: 'flex', alignItems: 'center' }}>
          <LucidchartIcon style={{ width: 20, height: 20 }} />
        </FlexBox>
      );
    }
    return type;
  },
});

export const getNameColumn = (
  sortById: SortById,
  sortOrder: SortOrder | false | null | undefined,
  sortBy: SortByFn,
  title: string = 'Name'
): Column<ExpandableRowData<any>> => ({
  title,
  dataIndex: 'name',
  headerStyle: { paddingBottom: s8, width: '70%' },
  sortOrder: sortById === 'name' ? sortOrder : false,
  onHeaderClick: () => sortBy('name'),
  valueRender: (_val: any, rowData: ExternalDocumentTableRow) => {
    const name = rowData.name;
    const url = rowData.url;

    return (
      <Link href={url} target="_blank" rel="noopener noreferrer">
        {name}
      </Link>
    );
  },
});

export const getCreatedByColumn = (
  sortById: SortById,
  sortOrder: SortOrder | false | null | undefined,
  sortBy: SortByFn,
  title: string = 'Created by'
): Column<ExternalDocumentTableRow> => ({
  title,
  dataIndex: 'createdByName',
  headerStyle: { paddingBottom: s8, width: '25%' },
  sortOrder: sortById === 'createdBy' ? sortOrder : false,
  onHeaderClick: () => sortBy('createdBy'),
});

export type ExternalDocumentColumnLabels = {
  type?: string;
  name?: string;
  createdBy?: string;
};

export const getDocumentColumns = (
  sortById: SortById,
  sortOrder: SortOrder | false | null | undefined,
  sortBy: SortByFn,
  columnLabels: ExternalDocumentColumnLabels = {}
): Column<ExternalDocumentTableRow>[] =>
  [
    getTypeColumn(sortById, sortOrder, sortBy, columnLabels.type),
    getNameColumn(sortById, sortOrder, sortBy, columnLabels.name),
    getCreatedByColumn(sortById, sortOrder, sortBy, columnLabels.createdBy),
  ].filter(isTruthy);

export type ExternalDocumentsWidgetLabels = {
  title?: (count: number) => ReactNode;
  emptyTitle?: string;
  emptyMessage?: ReactNode;
  columnLabels?: ExternalDocumentColumnLabels;
};

const ExternalDocumentWidget = ({
  externalDocuments,
  withBorder = false,
  labels,
}: {
  externalDocuments: PersistedExternalDocument[];
  withBorder?: boolean;
  labels?: ExternalDocumentsWidgetLabels;
}) => {
  const [perPage, setPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');

  if (externalDocuments.length === 0) {
    return null;
  }

  const filtered = filterDocumentsBySearchTerm(externalDocuments, searchTerm);

  const locale = getCurrentLocale();
  const data = filtered
    .map(toExternalDocumentTableRow)
    .sort((a, b) => localeCompare(a.name, b.name, locale));

  return (
    <HeightControl>
      <Island
        title={
          <Header3>
            {labels?.title?.(filtered.length) ??
              `Linked Documents (${filtered.length})`}
          </Header3>
        }
        rightContent={
          <SearchInput
            value={searchTerm}
            onValueChange={setSearchTerm}
            data-testid="external-doc-search"
          />
        }
        withBorder={withBorder}
        maxWidth="100%"
        minWidth="100%"
        bodySpacing="none"
        showHeaderBorder={false}
      >
        <PaginationController
          dataSource={data}
          perPage={perPage}
          render={({
            currentPageDataSource,
            currentPageNumber,
            totalResults,
            perPage,
            onPageSelect,
            sortById,
            sortOrder,
            sortBy,
          }) => (
            <>
              <DatasourceTable
                dataSource={currentPageDataSource}
                columns={getDocumentColumns(
                  sortById,
                  sortOrder,
                  sortBy,
                  labels?.columnLabels
                )}
                components={newTableTheme}
                getRowComponent={_ => Row}
                renderEmptyTable={() => (
                  <LilInfoDoq
                    doqType={DoqType.SIMPLE}
                    title={labels?.emptyTitle ?? 'No results'}
                    message={
                      labels?.emptyMessage ??
                      'No documents match your criteria.'
                    }
                  />
                )}
              />
              <Box paddingX="small" paddingY="medium">
                <PaginationBar
                  currentPageNumber={currentPageNumber}
                  perPage={perPage}
                  totalResults={totalResults}
                  pageSizeOptions={[5, 10, 25, 50]}
                  showPerPageSelector={totalResults > Math.min(5, perPage)}
                  onChange={({ currentPageNumber, perPage }) => {
                    setPerPage(perPage);
                    onPageSelect(currentPageNumber);
                  }}
                />
              </Box>
            </>
          )}
        />
      </Island>
    </HeightControl>
  );
};

export { ExternalDocumentWidget };
